import React from 'react';
import { motion } from 'framer-motion';
import { SPECIAL_MILESTONES, MILESTONE_CATEGORIES, MILESTONE_ICONS } from '../data/loveData';

const MilestoneCard = ({ milestone, date, daysToMilestone, isAchieved, language, index }) => {
  const isSpecial = SPECIAL_MILESTONES.includes(milestone);
  const isYearly = MILESTONE_CATEGORIES.YEARLY.includes(milestone);
  
  const getMilestoneCategory = () => {
    if (MILESTONE_CATEGORIES.FIRST_MOMENTS.includes(milestone)) return 'first-moments';
    if (MILESTONE_CATEGORIES.EARLY_JOURNEY.includes(milestone)) return 'early-journey';
    if (MILESTONE_CATEGORIES.YEARLY.includes(milestone)) return 'yearly';
    if (MILESTONE_CATEGORIES.NUMERIC_SPECIAL.includes(milestone)) return 'numeric-special';
    if (MILESTONE_CATEGORIES.CULTURAL_SPECIAL.includes(milestone)) return 'cultural-special';
    return 'default';
  };
  
  const getMilestoneEmoji = (milestone) => {
    return MILESTONE_ICONS[milestone] || MILESTONE_ICONS.default;
  };

  const getSpecialText = (milestone) => {
    if (!isSpecial) return '';
    
    const specialTexts = {
      520: { zh: '💞 我愛你', en: '💞 I Love You' },
      666: { zh: '💞 一路順風', en: '💞 Smooth Sailing' },
      888: { zh: '💞 發發發', en: '💞 Prosperity' },
      999: { zh: '💞 天長地久', en: '💞 Everlasting' },
      1314: { zh: '💞 一生一世', en: '💞 Forever & Ever' },
      2222: { zh: '💞 愛你愛你', en: '💞 Love You Love You' },
      3333: { zh: '💞 三生三世', en: '💞 Three Lifetimes' },
      5200: { zh: '💞 我愛你 x10', en: '💞 I Love You x10' },
      6666: { zh: '💞 六六大順', en: '💞 Smooth Fortune' },
      8888: { zh: '💞 幸福發發發發', en: '💞 Infinite Fortune' },
      9999: { zh: '💞 長長久久', en: '💞 Forever Lasting' }
    };

    return specialTexts[milestone]?.[language] || '';
  };

  const formatDate = (date) => {
    if (language === 'zh') {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    } else {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  };

  const category = getMilestoneCategory();
  
  // Base styling
  const baseCardClass = 'milestone-card relative p-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300';
  const cardStateClass = isAchieved ? 'achieved' : 'future';
  
  // Category-specific styling
  let categoryClass = '';
  switch(category) {
    case 'first-moments':
      categoryClass = 'first-moments-milestone';
      break;
    case 'early-journey':
      categoryClass = 'early-journey-milestone';
      break;
    case 'yearly':
      categoryClass = 'yearly-milestone';
      break;
    case 'numeric-special':
      categoryClass = 'numeric-special-milestone';
      break;
    case 'cultural-special':
      categoryClass = 'cultural-special-milestone';
      break;
    default:
      categoryClass = '';
  }
  
  // Background and border styling based on achievement and special status
  let cardBgClass, cardBorderClass, glowEffect = '';
  
  if (isAchieved) {
    if (isSpecial) {
      cardBgClass = 'bg-gradient-to-r from-amber-50 to-white dark:from-gray-700 dark:to-gray-750';
      cardBorderClass = 'border-2 border-amber-300 dark:border-amber-600';
      glowEffect = 'animate-glow-subtle';
    } else if (isYearly) {
      cardBgClass = 'bg-gradient-to-r from-blue-50 to-white dark:from-gray-700 dark:to-gray-750';
      cardBorderClass = 'border-2 border-blue-300 dark:border-blue-600';
    } else {
      cardBgClass = 'bg-gray-50 dark:bg-gray-750';
      cardBorderClass = 'border border-love-200 dark:border-love-700';
    }
  } else {
    if (isSpecial) {
      cardBgClass = 'bg-white dark:bg-gray-700';
      cardBorderClass = 'border border-amber-200 dark:border-amber-800';
    } else if (isYearly) {
      cardBgClass = 'bg-white dark:bg-gray-700';
      cardBorderClass = 'border border-blue-200 dark:border-blue-800';
    } else {
      cardBgClass = 'bg-white dark:bg-gray-700';
      cardBorderClass = 'border border-love-100 dark:border-love-800';
    }
  }

  // Text styling based on achievement and special status
  const milestoneNumberClass = isAchieved ? 
    (isSpecial ? 'text-amber-600 dark:text-amber-400 font-bold' : 
     isYearly ? 'text-blue-600 dark:text-blue-400 font-bold' : 
     'text-gray-700 dark:text-gray-300 font-bold') : 
    'text-gray-500 dark:text-gray-400 font-medium';

  const specialText = getSpecialText(milestone);
  
  // Achievement badge/ribbon for achieved milestones
  const achievementBadge = isAchieved && (
    <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center 
      ${isSpecial ? 'bg-amber-500 dark:bg-amber-600' : 
       isYearly ? 'bg-blue-500 dark:bg-blue-600' : 
       'bg-love-500 dark:bg-love-600'} 
      text-white shadow-md z-10`}
    >
      <span className="text-xs">✓</span>
    </div>
  );

  return (
    <motion.div
      className={`${baseCardClass} ${cardStateClass} ${categoryClass} ${cardBgClass} ${cardBorderClass} ${glowEffect}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
    >
      {achievementBadge}
      
      <div className="flex items-center mb-1">
        <span className={`emoji text-xl mr-1 ${isAchieved && 'animate-bounce-mini'}`}>
          {getMilestoneEmoji(milestone)}
        </span>
        <span className={`text-xl ${milestoneNumberClass}`}>
          {milestone}{language === 'zh' ? '天' : ''}
        </span>
      </div>
      
      {specialText && (
        <div className={`text-xs mt-1 font-bold ${isAchieved ? 'text-amber-600 dark:text-amber-400' : 'text-amber-500 dark:text-amber-500'}`}>
          {specialText}
        </div>
      )}
      
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {formatDate(date)}
      </div>
      
      <div className={`mt-2 text-sm font-medium ${
        isAchieved ? 
          (isSpecial ? 'text-amber-500 dark:text-amber-400' : 
           isYearly ? 'text-blue-500 dark:text-blue-400' : 
           'text-love-500 dark:text-love-400') : 
          'text-gray-500 dark:text-gray-400'
      }`}>
        {isAchieved ? 
          (language === 'zh' ? '已達成' : 'Achieved') :
          `${language === 'zh' ? '還有' : 'In'} ${daysToMilestone} ${language === 'zh' ? '天' : 'days'}`
        }
      </div>
    </motion.div>
  );
};

export default MilestoneCard;